import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { ForgeListRow, ForgeSectionHeader, ForgeFilterPillRow, ForgeMetricDisplay, type ForgeColor } from '@/components/ui/ForgeUI';
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { toast } from 'sonner';
import { useSubscription } from "@/hooks/useSubscription";
import { useLocation } from "wouter";
import {
  Camera,
  Upload,
  Plus,
  Trash2,
  BookOpen,
  Search,
  Loader2,
  X,
  ChevronDown,
  ChevronUp,
  Flame,
  Beef,
  Wheat,
  Droplets,
  Save,
  CheckCircle2,
  Settings,
  Dumbbell,
  Zap,
  TrendingUp,
  AlertCircle,
  Clock,
  SortAsc,
  Tag,
  ArrowUpDown,
  ChevronRight,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ScannedFood {
  name: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  servingSizeDesc: string;
}

interface NutritionEntry {
  id: string;
  name: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  servings: number;
  loggedAt: number;
}

// ─── Macro Bar ────────────────────────────────────────────────────────────────

function MacroBar({
  label,
  value,
  goal,
  color,
  unit = "g",
  icon: Icon,
}: {
  label: string;
  value: number;
  goal?: number;
  color: string;
  unit?: string;
  icon: React.ElementType;
}) {
  const pct = goal ? Math.min((value / goal) * 100, 100) : 0;
  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between mb-1">
        <span className="flex items-center gap-1 text-[9px] font-mono uppercase text-muted-foreground">
          <Icon className="h-3 w-3" />
          {label}
        </span>
        <span className="text-[10px] font-mono font-bold">
          {Math.round(value)}
          <span className="text-muted-foreground text-[9px]">{unit}</span>
        </span>
      </div>
      {goal && (
        <div className="h-1 bg-secondary rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${color}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  );
}

// ─── Macro Pill ───────────────────────────────────────────────────────────────

function MacroPill({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="flex flex-col items-center">
      <span className={`font-mono text-xs font-bold ${color}`}>{Math.round(value)}</span>
      <span className="text-[8px] font-mono text-muted-foreground uppercase">{label}</span>
    </div>
  );
}

// ─── Serving Adjuster Modal ───────────────────────────────────────────────────

function ServingAdjusterModal({
  food,
  onConfirm,
  onClose,
  onSaveToLibrary,
}: {
  food: ScannedFood;
  onConfirm: (entry: NutritionEntry) => void;
  onClose: () => void;
  onSaveToLibrary?: (food: ScannedFood) => void;
}) {
  const [servings, setServings] = useState(1);
  const [name, setName] = useState(food.name);

  const scaled = {
    calories: food.calories * servings,
    protein: food.protein * servings,
    carbs: food.carbs * servings,
    fat: food.fat * servings,
  };

  const handleConfirm = () => {
    const entry: NutritionEntry = {
      id: `entry-${Date.now()}`,
      name,
      calories: food.calories,
      protein: food.protein,
      carbs: food.carbs,
      fat: food.fat,
      servings,
      loggedAt: Date.now(),
    };
    onConfirm(entry);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm bg-card border-border">
        <DialogHeader>
          <DialogTitle className="font-mono text-sm uppercase tracking-wider">Log Food</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Name */}
          <div>
            <Label className="text-[10px] font-mono uppercase text-muted-foreground">Food Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 font-mono text-sm h-8"
            />
          </div>

          {/* Serving size info */}
          {food.servingSizeDesc && (
            <p className="text-[10px] font-mono text-muted-foreground">
              Per serving: {food.servingSizeDesc}
            </p>
          )}

          {/* Servings slider */}
          <div>
            <Label className="text-[10px] font-mono uppercase text-muted-foreground">
              Servings: <span className="text-foreground font-bold">{servings}x</span>
            </Label>
            <div className="flex items-center gap-2 mt-1">
              <Button
                variant="outline"
                size="icon"
                className="h-7 w-7 shrink-0"
                onClick={() => setServings((s) => Math.max(0.25, parseFloat((s - 0.25).toFixed(2))))}
              >
                <ChevronDown className="h-3 w-3" />
              </Button>
              <Input
                type="number"
                min={0.25}
                step={0.25}
                value={servings}
                onChange={(e) => setServings(Math.max(0.25, parseFloat(e.target.value) || 1))}
                className="font-mono text-sm h-7 text-center"
              />
              <Button
                variant="outline"
                size="icon"
                className="h-7 w-7 shrink-0"
                onClick={() => setServings((s) => parseFloat((s + 0.25).toFixed(2)))}
              >
                <ChevronUp className="h-3 w-3" />
              </Button>
            </div>
          </div>

          {/* Scaled macros preview */}
          <div className="grid grid-cols-4 gap-2 p-2 bg-secondary/30 rounded-sm border border-border/50">
            {[
              { label: "CAL", val: scaled.calories, unit: "", color: "text-orange-400" },
              { label: "PRO", val: scaled.protein, unit: "g", color: "text-blue-400" },
              { label: "CARB", val: scaled.carbs, unit: "g", color: "text-yellow-400" },
              { label: "FAT", val: scaled.fat, unit: "g", color: "text-red-400" },
            ].map(({ label, val, unit, color }) => (
              <div key={label} className="text-center">
                <div className={`font-mono text-xs font-bold ${color}`}>
                  {Math.round(val)}{unit}
                </div>
                <div className="text-[8px] font-mono text-muted-foreground uppercase">{label}</div>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 font-mono text-xs h-8"
              onClick={onClose}
            >
              Cancel
            </Button>
            {onSaveToLibrary && (
              <Button
                variant="outline"
                size="sm"
                className="font-mono text-xs h-8 border-primary/40 text-primary hover:bg-primary/10"
                onClick={() => onSaveToLibrary({ ...food, name })}
              >
                <BookOpen className="h-3 w-3 mr-1" />
                Save
              </Button>
            )}
            <Button
              size="sm"
              className="flex-1 font-mono text-xs h-8"
              onClick={handleConfirm}
            >
              Log It
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface NutritionTrackerProps {
  selectedDate: string;
  clientId?: number; // When set, show client's macro summary only (trainer view — read-only)
}

export default function NutritionTracker({ selectedDate, clientId }: NutritionTrackerProps) {
  const isClientView = !!clientId;
  const utils = trpc.useUtils();
  const { isPaid } = useSubscription();
  const [, navigate] = useLocation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // State
  const [pendingFood, setPendingFood] = useState<ScannedFood | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [librarySearch, setLibrarySearch] = useState("");
  const [libraryCategory, setLibraryCategory] = useState<string>('all');
  const [librarySort, setLibrarySort] = useState<'recent' | 'az' | 'protein' | 'calories'>('recent');
  const [overrideFoodId, setOverrideFoodId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Goal editing state
  const [showGoalEditor, setShowGoalEditor] = useState(false);
  const [goalInputs, setGoalInputs] = useState({ protein: 150, carbs: 200, fat: 65 });

  // Fuel Preferences (persisted to localStorage)
  const [fuelPrefs, setFuelPrefs] = useState(() => {
    try {
      const saved = localStorage.getItem('fuelPrefs');
      if (saved) return JSON.parse(saved);
    } catch {}
    return {
      trainingRestTargets: false,
      proteinPacing: true,
      prePostWorkout: false,
      sevenDayTrend: true,
    };
  });
  const updatePref = (key: string, value: boolean) => {
    const next = { ...fuelPrefs, [key]: value };
    setFuelPrefs(next);
    try { localStorage.setItem('fuelPrefs', JSON.stringify(next)); } catch {}
  };

  // Pre/Post workout collapsible state
  const [preSlotOpen, setPreSlotOpen] = useState(false);
  const [postSlotOpen, setPostSlotOpen] = useState(false);

  // Queries
  const { data: ownLog, isLoading: ownLogLoading } = trpc.nutrition.getLog.useQuery(
    { date: selectedDate },
    { enabled: !isClientView }
  );
  const { data: clientLog, isLoading: clientLogLoading } = trpc.trainerPortal.getClientNutritionLog.useQuery(
    { clientId: clientId!, date: selectedDate },
    { enabled: isClientView && !!clientId }
  );
  const log = isClientView ? clientLog : ownLog;
  const logLoading = isClientView ? clientLogLoading : ownLogLoading;
  const { data: savedFoods = [] } = trpc.nutrition.listSavedFoods.useQuery(undefined, { enabled: !isClientView });
  const { data: profile } = trpc.profile.get.useQuery(undefined, { enabled: !isClientView });
  // Fuel intelligence (always fetched — data pipeline never breaks regardless of toggle state)
  const { data: fuelIntel } = trpc.nutrition.getFuelIntelligence.useQuery(
    { date: selectedDate },
    { enabled: !isClientView }
  );

  // Mutations
  const logFoodMut = trpc.nutrition.logFood.useMutation({
    onSuccess: () => {
      utils.nutrition.getLog.invalidate();
      // Also invalidate month totals so calendar cards update
      const yearMonth = selectedDate.slice(0, 7);
      utils.nutrition.getMonthTotals.invalidate({ yearMonth });
    },
  });
  const scanLabelMut = trpc.nutrition.scanLabel.useMutation();
  const saveFoodMut = trpc.nutrition.saveFood.useMutation({
    onSuccess: () => utils.nutrition.listSavedFoods.invalidate(),
  });
  const deleteFoodMut = trpc.nutrition.deleteFood.useMutation({
    onSuccess: () => utils.nutrition.listSavedFoods.invalidate(),
  });
  const updateFoodCategoryMut = trpc.nutrition.updateFoodCategory.useMutation({
    onSuccess: () => {
      utils.nutrition.listSavedFoods.invalidate();
      setOverrideFoodId(null);
      toast.success('Category updated');
    },
  });
  const saveProfileMut = trpc.profile.save.useMutation({
    onSuccess: () => {
      utils.profile.get.invalidate();
      const yearMonth = selectedDate.slice(0, 7);
      utils.nutrition.getMonthTotals.invalidate({ yearMonth });
      setShowGoalEditor(false);
      toast.success('Macro goals updated');
    },
  });

  // Sync goal inputs when profile loads
  useEffect(() => {
    if (profile) {
      setGoalInputs({
        protein: (profile as any).targetProtein ?? 150,
        carbs: (profile as any).targetCarbs ?? 200,
        fat: (profile as any).targetFat ?? 65,
      });
    }
  }, [profile]);

  const entries: NutritionEntry[] = (log?.entries as NutritionEntry[]) ?? [];

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleImageFile = useCallback(async (file: File) => {
    if (!file) return;
    setIsScanning(true);
    try {
      const reader = new FileReader();
      const dataUrl = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const result = await scanLabelMut.mutateAsync({ imageUrl: dataUrl });
      setPendingFood(result);
    } catch {
      toast.error("Scan failed: Could not read the nutrition label. Try a clearer photo.");
    } finally {
      setIsScanning(false);
    }
  }, [scanLabelMut]);

  const handleConfirmEntry = async (entry: NutritionEntry) => {
    const newEntries = [...entries, entry];
    setIsSaving(true);
    try {
      await logFoodMut.mutateAsync({ date: selectedDate, entries: newEntries });
      setPendingFood(null);
      toast.success(
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
          <div>
            <p className="font-mono text-xs font-bold">{entry.name} saved to daily log</p>
            <p className="text-[10px] text-muted-foreground">
              +{Math.round(entry.calories * entry.servings)} kcal ·{" "}
              {Math.round(entry.protein * entry.servings)}g P ·{" "}
              {Math.round(entry.carbs * entry.servings)}g C ·{" "}
              {Math.round(entry.fat * entry.servings)}g F
            </p>
          </div>
        </div>
      );
    } catch {
      toast.error("Failed to save entry. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveToLibrary = async (food: ScannedFood) => {
    await saveFoodMut.mutateAsync({
      name: food.name,
      caloriesPerServing: food.calories,
      proteinPerServing: food.protein,
      carbsPerServing: food.carbs,
      fatPerServing: food.fat,
      servingSizeDesc: food.servingSizeDesc,
    });
    toast.success(`${food.name} saved to your food library`);
  };

  const handleQuickAddFromLibrary = (food: typeof savedFoods[0]) => {
    setPendingFood({
      name: food.name,
      calories: food.caloriesPerServing,
      protein: food.proteinPerServing,
      carbs: food.carbsPerServing,
      fat: food.fatPerServing,
      servingSizeDesc: food.servingSizeDesc ?? "",
    });
    setShowLibrary(false);
  };

  const handleRemoveEntry = async (entryId: string) => {
    const newEntries = entries.filter((e) => e.id !== entryId);
    await logFoodMut.mutateAsync({ date: selectedDate, entries: newEntries });
    toast.success("Entry removed");
  };

  // ─── Totals ──────────────────────────────────────────────────────────────────

  const totals = {
    calories: entries.reduce((s, e) => s + e.calories * e.servings, 0),
    protein: entries.reduce((s, e) => s + e.protein * e.servings, 0),
    carbs: entries.reduce((s, e) => s + e.carbs * e.servings, 0),
    fat: entries.reduce((s, e) => s + e.fat * e.servings, 0),
  };

  // ─── Food Library: auto-categorization helper ───────────────────────────────
  const FOOD_CATEGORIES = [
    { key: 'all', label: 'All' },
    { key: 'protein_sources', label: 'Protein Sources' },
    { key: 'grains_carbs', label: 'Grains & Carbs' },
    { key: 'fruits_veg', label: 'Fruits & Vegetables' },
    { key: 'dairy', label: 'Dairy' },
    { key: 'snacks_bars', label: 'Snacks & Bars' },
    { key: 'meals', label: 'Meals' },
    { key: 'supplements', label: 'Supplements' },
    { key: 'custom', label: 'Custom' },
  ];

  function autoCategory(food: { name: string; proteinPerServing: number; carbsPerServing: number; fatPerServing: number; caloriesPerServing: number; servingSizeDesc?: string | null }): string {
    const { name, proteinPerServing: p, carbsPerServing: c, fatPerServing: f, caloriesPerServing: cal, servingSizeDesc } = food;
    const nameLower = name.toLowerCase();
    const descLower = (servingSizeDesc ?? '').toLowerCase();
    // "menu item" tag in servingSizeDesc → Meals
    const isMenuItem = descLower.includes('menu item');
    // Dairy keyword match
    const dairyKeywords = ['yogurt', 'milk', 'cheese', 'whey', 'cottage', 'kefir', 'cream'];
    const isDairy = dairyKeywords.some(kw => nameLower.includes(kw));
    // Supplement: very low calories with meaningful protein
    if (cal <= 30 && p >= 5) return 'supplements';
    // Menu items → Meals
    if (isMenuItem) return 'meals';
    const total = p * 4 + c * 4 + f * 9;
    if (total === 0) return 'custom';
    const proteinPct = (p * 4) / total;
    const carbPct = (c * 4) / total;
    const fatPct = (f * 9) / total;
    // Dairy keyword match takes priority over macro rules
    if (isDairy) return 'dairy';
    // Protein-dominant (>= 30% protein cals) → Protein Sources
    if (proteinPct >= 0.30) return 'protein_sources';
    // Fat-dominant (>= 40% fat cals) → Snacks & Bars
    if (fatPct >= 0.40) return 'snacks_bars';
    // Carb-dominant (>= 50% carb cals) → Grains & Carbs
    if (carbPct >= 0.50) return 'grains_carbs';
    // Default fallback → Snacks & Bars
    return 'snacks_bars';
  }

  const filteredLibrary = savedFoods
    .filter((f) => f.name.toLowerCase().includes(librarySearch.toLowerCase()))
    .filter((f) => {
      if (libraryCategory === 'all') return true;
      const effectiveCategory = f.category ?? autoCategory(f);
      return effectiveCategory === libraryCategory;
    })
    .sort((a, b) => {
      if (librarySort === 'az') return a.name.localeCompare(b.name);
      if (librarySort === 'protein') return b.proteinPerServing - a.proteinPerServing;
      if (librarySort === 'calories') return a.caloriesPerServing - b.caloriesPerServing;
      // 'recent': default — already ordered by updatedAt desc from server
      return 0;
    });

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-mono text-sm font-bold uppercase tracking-wider text-primary">FUEL</h2>
          <p className="text-[10px] font-mono text-muted-foreground uppercase">{selectedDate}</p>
          {isClientView && <p className="text-[9px] font-mono text-amber-400 uppercase tracking-wider mt-0.5">READ ONLY — MACRO SUMMARY</p>}
        </div>
        {!isClientView && <div className="flex gap-1.5">
          <Button
            variant="outline"
            size="sm"
            className="h-7 font-mono text-[10px] uppercase gap-1"
            onClick={() => setShowLibrary(true)}
          >
            <BookOpen className="h-3 w-3" />
            Library
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 font-mono text-[10px] uppercase gap-1"
            disabled={isScanning || !isPaid}
            onClick={() => isPaid ? fileInputRef.current?.click() : navigate("/pricing")}
            title={!isPaid ? "Upgrade to use AI label scanning" : undefined}
          >
            {isScanning ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
            Upload
          </Button>
          <Button
            size="sm"
            className="h-7 font-mono text-[10px] uppercase gap-1"
            disabled={isScanning || !isPaid}
            onClick={() => isPaid ? cameraInputRef.current?.click() : navigate("/pricing")}
            title={!isPaid ? "Upgrade to use AI label scanning" : undefined}
          >
            {isScanning ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />}
            Scan Label{!isPaid && " ★"}
          </Button>
          {/* Fuel Preferences Sheet */}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" title="Fuel Preferences">
                <Settings className="h-3.5 w-3.5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 bg-card border-border">
              <SheetHeader className="mb-6">
                <SheetTitle className="font-mono text-sm uppercase tracking-wider">FUEL PREFERENCES</SheetTitle>
              </SheetHeader>
              <div className="space-y-5">
                {/* Toggle 1 */}
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-mono text-xs font-bold uppercase">Training / Rest Day Targets</p>
                    <p className="text-[10px] font-mono text-muted-foreground mt-0.5">Adjusts carb and calorie targets based on your active split. Protein stays constant.</p>
                  </div>
                  <Switch
                    checked={fuelPrefs.trainingRestTargets}
                    onCheckedChange={(v) => updatePref('trainingRestTargets', v)}
                    className="shrink-0 mt-0.5"
                  />
                </div>
                {/* Toggle 2 */}
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-mono text-xs font-bold uppercase">Protein Pacing Alerts</p>
                    <p className="text-[10px] font-mono text-muted-foreground mt-0.5">Shows a live pacing line below your macro totals. Tells you how much protein you need in remaining meals.</p>
                  </div>
                  <Switch
                    checked={fuelPrefs.proteinPacing}
                    onCheckedChange={(v) => updatePref('proteinPacing', v)}
                    className="shrink-0 mt-0.5"
                  />
                </div>
                {/* Toggle 3 */}
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-mono text-xs font-bold uppercase">Pre / Post Workout Meal Slots</p>
                    <p className="text-[10px] font-mono text-muted-foreground mt-0.5">Auto-detects today's session. Prompts post-workout protein if you trained but haven't logged food in the 2-hour window.</p>
                  </div>
                  <Switch
                    checked={fuelPrefs.prePostWorkout}
                    onCheckedChange={(v) => updatePref('prePostWorkout', v)}
                    className="shrink-0 mt-0.5"
                  />
                </div>
                {/* Toggle 4 */}
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-mono text-xs font-bold uppercase">7-Day Nutrition Trend</p>
                    <p className="text-[10px] font-mono text-muted-foreground mt-0.5">Compact bar chart showing protein compliance over the last 7 days. Data always feeds the Recover tab regardless of this toggle.</p>
                  </div>
                  <Switch
                    checked={fuelPrefs.sevenDayTrend}
                    onCheckedChange={(v) => updatePref('sevenDayTrend', v)}
                    className="shrink-0 mt-0.5"
                  />
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>}
      </div>

      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => e.target.files?.[0] && handleImageFile(e.target.files[0])}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => e.target.files?.[0] && handleImageFile(e.target.files[0])}
      />

      {/* Daily Macro Summary — Running Total Panel */}
      {(() => {
        // Use adjusted targets when training/rest toggle is ON
        const baseGoals = {
          protein: (profile as any)?.targetProtein ?? 150,
          carbs: (profile as any)?.targetCarbs ?? 200,
          fat: (profile as any)?.targetFat ?? 65,
          calories: (profile as any)?.targetCalories ?? 2000,
        };
        const goals = fuelPrefs.trainingRestTargets && fuelIntel ? {
          protein: baseGoals.protein, // protein stays constant
          carbs: fuelIntel.adjustedCarbs,
          fat: baseGoals.fat,
          calories: fuelIntel.adjustedCalories,
        } : baseGoals;
        const calGoal = Math.round(goals.protein * 4 + goals.carbs * 4 + goals.fat * 9);
        const calPct = calGoal > 0 ? Math.min(100, Math.round((totals.calories / calGoal) * 100)) : 0;
        return (
          <div className="p-3 bg-secondary/20 border border-orange-400/20 rounded-sm space-y-2">
            {/* Header row: label + training/rest badge + gear */}
            <ForgeSectionHeader
              label="Daily Totals"
              count={entries.length}
              countLabel="entry"
              className="mb-1"
              action={
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowGoalEditor((v) => !v)}
                  title="Set macro goals"
                >
                  <Save className="h-3 w-3" />
                </Button>
              }
            />
            {fuelPrefs.trainingRestTargets && fuelIntel && (
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-[8px] font-mono font-bold uppercase px-1.5 py-0.5 rounded-sm tracking-wider ${
                  fuelIntel.isTrainingDay
                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                    : 'bg-zinc-500/20 text-zinc-400 border border-zinc-500/30'
                }`}>
                  {fuelIntel.isTrainingDay ? <><Dumbbell className="inline h-2.5 w-2.5 mr-0.5" />TRAINING DAY</> : 'REST DAY'}
                </span>
              </div>
            )}
            {/* Calorie hero number */}
            <ForgeMetricDisplay
              value={totals.calories}
              goal={calGoal}
              unit="KCAL"
              color="orange"
              size="lg"
              showBar
              className="py-1"
            />
            {/* Macro bars with goals */}
            <div className="flex gap-3">
              <MacroBar label="Protein" value={totals.protein} goal={goals.protein} color={totals.protein > goals.protein ? 'bg-orange-400' : 'bg-blue-400'} icon={Beef} />
              <MacroBar label="Carbs" value={totals.carbs} goal={goals.carbs} color={totals.carbs > goals.carbs ? 'bg-orange-400' : 'bg-yellow-400'} icon={Wheat} />
              <MacroBar label="Fat" value={totals.fat} goal={goals.fat} color={totals.fat > goals.fat ? 'bg-orange-400' : 'bg-red-400'} icon={Droplets} />
            </div>
            {/* X / Y progress text */}
            <div className="flex gap-3">
              <span className="flex-1 text-[9px] font-mono text-center text-blue-400">{Math.round(totals.protein)}<span className="text-muted-foreground">/{goals.protein}g P</span></span>
              <span className="flex-1 text-[9px] font-mono text-center text-yellow-400">{Math.round(totals.carbs)}<span className="text-muted-foreground">/{goals.carbs}g C</span></span>
              <span className="flex-1 text-[9px] font-mono text-center text-red-400">{Math.round(totals.fat)}<span className="text-muted-foreground">/{goals.fat}g F</span></span>
            </div>
            {/* Macro Goals Editor (inline) */}
            {showGoalEditor && (
              <div className="pt-2 border-t border-border/30 space-y-2">
                <p className="text-[9px] font-mono uppercase text-muted-foreground tracking-wider">Set Daily Macro Goals</p>
                <div className="grid grid-cols-3 gap-2">
                  {(['protein', 'carbs', 'fat'] as const).map((macro) => (
                    <div key={macro}>
                      <Label className="text-[9px] font-mono uppercase text-muted-foreground">{macro} (g)</Label>
                      <Input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        value={goalInputs[macro]}
                        onChange={(e) => setGoalInputs((prev) => ({ ...prev, [macro]: parseInt(e.target.value) || 0 }))}
                        className="h-7 font-mono text-xs mt-0.5"
                      />
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[9px] font-mono text-muted-foreground">
                    = {Math.round(goalInputs.protein * 4 + goalInputs.carbs * 4 + goalInputs.fat * 9)} kcal/day
                  </span>
                  <div className="flex gap-1.5">
                    <Button variant="ghost" size="sm" className="h-6 font-mono text-[9px]" onClick={() => setShowGoalEditor(false)}>Cancel</Button>
                    <Button
                      size="sm"
                      className="h-6 font-mono text-[9px]"
                      disabled={saveProfileMut.isPending}
                      onClick={() => {
                        const cal = Math.round(goalInputs.protein * 4 + goalInputs.carbs * 4 + goalInputs.fat * 9);
                        saveProfileMut.mutate({
                          targetProtein: goalInputs.protein,
                          targetCarbs: goalInputs.carbs,
                          targetFat: goalInputs.fat,
                          targetCalories: cal,
                        });
                      }}
                    >
                      {saveProfileMut.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save Goals'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Food Log — hidden in client view (macro summary only) */}
      {!isClientView && (logLoading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <Flame className="h-8 w-8 mx-auto mb-2 opacity-20" />
          <p className="text-[10px] font-mono uppercase">No foods logged yet</p>
          <p className="text-[9px] font-mono text-muted-foreground/60 mt-1">Scan a label or pick from your library</p>
          <p className="text-[9px] font-mono text-muted-foreground/40 mt-0.5">Multiple scans accumulate throughout the day</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {entries.map((entry) => {
            // Dominant macro = left-border accent color
            const pCal = entry.protein * entry.servings * 4;
            const cCal = entry.carbs * entry.servings * 4;
            const fCal = entry.fat * entry.servings * 9;
            const maxMacro = Math.max(pCal, cCal, fCal);
            const entryColor: ForgeColor = maxMacro === pCal ? 'blue' : maxMacro === cCal ? 'yellow' : 'red';
            return (
              <ForgeListRow
                key={entry.id}
                color={entryColor}
                title={<span className="font-mono text-xs font-bold">{entry.name}</span>}
                subtitle={
                  <>
                    {entry.servings}x serving · {Math.round(entry.calories * entry.servings)} kcal ·{" "}
                    <span className="text-blue-400">{Math.round(entry.protein * entry.servings)}g P</span>{" "}
                    <span className="text-yellow-400">{Math.round(entry.carbs * entry.servings)}g C</span>{" "}
                    <span className="text-red-400">{Math.round(entry.fat * entry.servings)}g F</span>
                  </>
                }
                action={
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-muted-foreground hover:text-destructive shrink-0 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                    onClick={() => handleRemoveEntry(entry.id)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                }
              />
            );
          })}
        </div>
      ))}

      {/* Feature 2: Protein Pacing Alert */}
      {!isClientView && fuelPrefs.proteinPacing && fuelIntel && (
        <div className={`p-3 rounded-sm border space-y-1.5 ${
          fuelIntel.proteinOnPace
            ? 'bg-green-500/10 border-green-500/20'
            : 'bg-blue-500/10 border-blue-500/20'
        }`}>
          <div className="flex items-center gap-1.5">
            {fuelIntel.proteinOnPace
              ? <CheckCircle2 className="h-3.5 w-3.5 text-green-400 shrink-0" />
              : <Zap className="h-3.5 w-3.5 text-blue-400 shrink-0" />}
            <span className="text-[9px] font-mono uppercase tracking-wider font-bold text-muted-foreground">PROTEIN PACING</span>
          </div>
          <p className="text-xs font-mono text-foreground">{fuelIntel.proteinPacingNote}</p>
          {!fuelIntel.proteinOnPace && (
            <div className="flex items-center gap-2 pt-0.5">
              <div className="flex-1 bg-secondary/40 rounded-full h-1">
                <div
                  className="h-1 rounded-full bg-blue-400 transition-all"
                  style={{ width: `${Math.min(100, Math.round((fuelIntel.loggedProtein / fuelIntel.baseProtein) * 100))}%` }}
                />
              </div>
              <span className="text-[9px] font-mono text-blue-400 shrink-0">
                {fuelIntel.loggedProtein}g / {fuelIntel.baseProtein}g
              </span>
            </div>
          )}
        </div>
      )}

      {/* Feature 3: Pre/Post Workout Meal Slots */}
      {!isClientView && fuelPrefs.prePostWorkout && (
        <div className="space-y-1.5">
          {/* Post-workout soft prompt (shown when trained but within 2h window) */}
          {fuelIntel?.postWorkoutWindowOpen && (
            <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-sm">
              <div className="flex items-center gap-1.5 mb-1">
                <AlertCircle className="h-3.5 w-3.5 text-amber-400 shrink-0" />
                <span className="text-[9px] font-mono uppercase tracking-wider font-bold text-amber-400">POST-WORKOUT WINDOW</span>
              </div>
              <p className="text-xs font-mono text-foreground">
                You trained {fuelIntel.hoursAgoTrained?.toFixed(1)}h ago{fuelIntel.sessionName ? ` (${fuelIntel.sessionName})` : ''} — did you get your post-workout protein in?
              </p>
            </div>
          )}
          {/* Pre-workout slot */}
          <div className="border border-border/30 rounded-sm overflow-hidden">
            <button
              className="w-full flex items-center justify-between p-2.5 bg-secondary/10 hover:bg-secondary/20 transition-colors"
              onClick={() => setPreSlotOpen((v) => !v)}
            >
              <div className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-[10px] font-mono uppercase tracking-wider font-bold">PRE-WORKOUT MEAL</span>
              </div>
              {preSlotOpen ? <ChevronUp className="h-3 w-3 text-muted-foreground" /> : <ChevronDown className="h-3 w-3 text-muted-foreground" />}
            </button>
            {preSlotOpen && (
              <div className="p-3 border-t border-border/20 space-y-1">
                <p className="text-[10px] font-mono text-muted-foreground">Log foods eaten 1–2 hours before your session. Focus on carbs + moderate protein.</p>
                <p className="text-[9px] font-mono text-muted-foreground/60">Target: ~{Math.round((fuelIntel?.adjustedCarbs ?? 200) * 0.25)}g carbs, ~{Math.round((fuelIntel?.baseProtein ?? 150) * 0.2)}g protein</p>
              </div>
            )}
          </div>
          {/* Post-workout slot */}
          <div className="border border-border/30 rounded-sm overflow-hidden">
            <button
              className="w-full flex items-center justify-between p-2.5 bg-secondary/10 hover:bg-secondary/20 transition-colors"
              onClick={() => setPostSlotOpen((v) => !v)}
            >
              <div className="flex items-center gap-1.5">
                <Dumbbell className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-[10px] font-mono uppercase tracking-wider font-bold">POST-WORKOUT MEAL</span>
                {fuelIntel?.trainedToday && <span className="text-[8px] font-mono text-green-400 bg-green-500/10 px-1 rounded">TRAINED TODAY</span>}
              </div>
              {postSlotOpen ? <ChevronUp className="h-3 w-3 text-muted-foreground" /> : <ChevronDown className="h-3 w-3 text-muted-foreground" />}
            </button>
            {postSlotOpen && (
              <div className="p-3 border-t border-border/20 space-y-1">
                <p className="text-[10px] font-mono text-muted-foreground">Log foods eaten within 2 hours after your session. Prioritize fast protein + carbs.</p>
                <p className="text-[9px] font-mono text-muted-foreground/60">Target: ~{Math.round((fuelIntel?.baseProtein ?? 150) * 0.25)}g protein, ~{Math.round((fuelIntel?.adjustedCarbs ?? 200) * 0.2)}g carbs</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Serving Adjuster Modal — hidden in client view */}
      {!isClientView && pendingFood && (
        <ServingAdjusterModal
          food={pendingFood}
          onConfirm={handleConfirmEntry}
          onClose={() => setPendingFood(null)}
          onSaveToLibrary={handleSaveToLibrary}
        />
      )}

      {/* Saved Foods Library Modal — hidden in client view */}
      {!isClientView && showLibrary && (
        <Dialog open onOpenChange={() => { setShowLibrary(false); setOverrideFoodId(null); }}>
          <DialogContent className="max-w-sm bg-card border-border max-h-[85vh] flex flex-col">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="font-mono text-sm uppercase tracking-wider">Food Library</DialogTitle>
                {/* Sort dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-7 px-2 font-mono text-[9px] uppercase gap-1">
                      <ArrowUpDown className="h-3 w-3" />
                      {librarySort === 'recent' ? 'Recent' : librarySort === 'az' ? 'A-Z' : librarySort === 'protein' ? 'Protein' : 'Calories'}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="font-mono text-xs">
                    <DropdownMenuRadioGroup value={librarySort} onValueChange={(v) => setLibrarySort(v as typeof librarySort)}>
                      <DropdownMenuRadioItem value="recent">Recent</DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="az">A-Z</DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="protein">Highest Protein</DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="calories">Lowest Calories</DropdownMenuRadioItem>
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </DialogHeader>

            {/* Search */}
            <div className="relative shrink-0">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
              <Input
                placeholder="Search foods..."
                value={librarySearch}
                onChange={(e) => setLibrarySearch(e.target.value)}
                className="pl-7 h-8 font-mono text-xs"
              />
            </div>

            {/* Category filter pills */}
            <ForgeFilterPillRow
              options={FOOD_CATEGORIES}
              value={libraryCategory}
              onChange={setLibraryCategory}
            />

            {/* Food list */}
            <div className="flex-1 overflow-y-auto min-h-0 rounded-sm border border-border/30 bg-card">
              {filteredLibrary.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <BookOpen className="h-6 w-6 mx-auto mb-2 opacity-20" />
                  <p className="text-[10px] font-mono uppercase">
                    {libraryCategory === 'all' ? 'No saved foods' : `No foods in ${FOOD_CATEGORIES.find(c => c.key === libraryCategory)?.label}`}
                  </p>
                  <p className="text-[9px] font-mono opacity-60 mt-1">Scan a label and tap "Save" to add foods here</p>
                </div>
              ) : (
                filteredLibrary.map((food) => {
                  const effectiveCat = food.category ?? autoCategory(food);
                  const catLabel = FOOD_CATEGORIES.find(c => c.key === effectiveCat)?.label ?? 'Custom';
                  const isOverriding = overrideFoodId === food.id;
                  return (
                    <div key={food.id} className="border-b border-border/30 last:border-b-0 overflow-hidden">
                      <div className="flex items-center">
                        {/* Tap anywhere on the row (except trash) to add */}
                        <button
                          className="flex-1 min-w-0 text-left px-3 py-2.5 hover:bg-secondary/30 active:bg-secondary/50 transition-colors"
                          onClick={() => handleQuickAddFromLibrary(food)}
                        >
                          <p className="font-mono text-xs font-semibold truncate">{food.name}</p>
                          <p className="text-[9px] font-mono text-muted-foreground mt-0.5">
                            {Math.round(food.caloriesPerServing)} kcal ·{" "}
                            <span className="text-blue-400">{Math.round(food.proteinPerServing)}g P</span>{" "}
                            <span className="text-yellow-400">{Math.round(food.carbsPerServing)}g C</span>{" "}
                            <span className="text-red-400">{Math.round(food.fatPerServing)}g F</span>
                            {food.servingSizeDesc && (
                              <span className="text-muted-foreground/60"> · {food.servingSizeDesc}</span>
                            )}
                          </p>
                          {/* Category override trigger — hidden from list view, shown via long-press / future detail view */}
                        </button>
                        {/* Trash — isolated from the tap-to-add zone */}
                        <button
                          className="shrink-0 px-3 py-2.5 text-muted-foreground/40 hover:text-destructive transition-colors"
                          onClick={(e) => { e.stopPropagation(); deleteFoodMut.mutate({ foodId: food.id }); }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      {/* Category override picker */}
                      {isOverriding && (
                        <div className="px-2 pb-2 border-t border-border/30 pt-2">
                          <p className="text-[8px] font-mono text-muted-foreground uppercase mb-1.5">Change category:</p>
                          <div className="flex flex-wrap gap-1">
                            {FOOD_CATEGORIES.filter(c => c.key !== 'all').map((cat) => (
                              <button
                                key={cat.key}
                                onClick={() => updateFoodCategoryMut.mutate({ foodId: food.id, category: cat.key })}
                                className={`px-2 py-0.5 rounded-full font-mono text-[8px] uppercase border transition-colors ${
                                  effectiveCat === cat.key
                                    ? 'bg-primary text-primary-foreground border-primary'
                                    : 'bg-secondary/30 text-muted-foreground border-border/40 hover:border-primary/50'
                                }`}
                              >
                                {cat.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Feature 4: 7-Day Nutrition Trend Chart (always fetched, display gated by toggle) */}
      {!isClientView && fuelPrefs.sevenDayTrend && fuelIntel?.sevenDayTrend && fuelIntel.sevenDayTrend.length > 0 && (
        <div className="p-3 bg-secondary/20 border border-border/30 rounded-sm space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <TrendingUp className="h-3.5 w-3.5 text-primary" />
              <span className="text-[9px] font-mono uppercase tracking-wider font-bold text-muted-foreground">7-DAY PROTEIN TREND</span>
            </div>
            <div className="flex items-center gap-2 text-[8px] font-mono text-muted-foreground">
              <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm bg-green-500"></span>≥90%</span>
              <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm bg-yellow-500"></span>70-89%</span>
              <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm bg-red-500"></span>&lt;70%</span>
            </div>
          </div>
          <div className="flex items-end gap-1 h-16">
            {fuelIntel.sevenDayTrend.map((day, i) => {
              const pct = day.proteinPct;
              const barColor = pct >= 90 ? 'bg-green-500' : pct >= 70 ? 'bg-yellow-500' : 'bg-red-500';
              const barH = Math.max(8, Math.round((pct / 100) * 56)); // max 56px (h-14)
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                  <div
                    className={`w-full rounded-sm ${barColor} transition-all`}
                    style={{ height: `${barH}px` }}
                    title={`${day.date}: ${day.protein}g protein (${pct}%)`}
                  />
                  <span className="text-[8px] font-mono text-muted-foreground/60">{day.date.slice(5)}</span>
                </div>
              );
            })}
          </div>
          <p className="text-[9px] font-mono text-muted-foreground/60">
            Avg: {Math.round(fuelIntel.sevenDayTrend.reduce((s, d) => s + d.proteinPct, 0) / fuelIntel.sevenDayTrend.length)}% protein target over last 7 days
          </p>
        </div>
      )}

      {/* Saving overlay */}
      {isSaving && (
        <div className="fixed inset-0 bg-background/50 flex items-center justify-center z-50 pointer-events-none">
          <div className="flex items-center gap-2 bg-card border border-border px-4 py-2 rounded-sm shadow-lg">
            <Save className="h-4 w-4 text-orange-400 animate-pulse" />
            <span className="font-mono text-xs text-muted-foreground">Saving to daily log...</span>
          </div>
        </div>
      )}
    </div>
  );
}
